import React, { useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiService, { Company } from "../services/api";
import { getS3Url } from "../utils/imageUtils";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("No se pudo leer la imagen"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.readAsDataURL(file);
  });

const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;

export const companyImageSrc = (
  company: Pick<Company, "imageS3Key" | "imageUrl">,
): string => getS3Url(company.imageS3Key) || company.imageUrl || "";

interface CompanyImageEditorProps {
  company: Company;
  onSuccess?: (message: string) => void;
}

const CompanyImageEditor: React.FC<CompanyImageEditorProps> = ({
  company,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const currentSrc = companyImageSrc(company);

  const applyResult = (
    result: { imageS3Key: string | null; imageUrl: null },
    message: string,
  ) => {
    queryClient.setQueryData<Company>(
      ["company-detail", company._id],
      (prev) =>
        prev
          ? {
              ...prev,
              imageS3Key: result.imageS3Key ?? undefined,
              imageUrl: undefined,
            }
          : prev,
    );
    void queryClient.invalidateQueries({ queryKey: ["company", company._id] });
    void queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
    setImageUrl("");
    setError(null);
    setOpen(false);
    onSuccess?.(message);
  };

  const uploadMutation = useMutation({
    mutationFn: async (
      payload: { file: File } | { url: string },
    ) => {
      if ("file" in payload) {
        const imageBase64 = await fileToBase64(payload.file);
        return apiService.uploadCompanyImage(company._id, { imageBase64 });
      }
      return apiService.uploadCompanyImage(company._id, {
        imageUrl: payload.url,
      });
    },
    onSuccess: (result) => applyResult(result, "Imagen de empresa actualizada"),
    onError: (e: any) =>
      setError(apiErrorMessage(e, "No se pudo subir la imagen")),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteCompanyImage(company._id),
    onSuccess: (result) => applyResult(result, "Imagen de empresa eliminada"),
    onError: (e: any) =>
      setError(apiErrorMessage(e, "No se pudo eliminar la imagen")),
  });

  const busy = uploadMutation.isPending || deleteMutation.isPending;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("El archivo debe ser una imagen");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("La imagen supera el límite de 8MB");
      return;
    }
    setError(null);
    uploadMutation.mutate({ file });
  };

  const handleClose = () => {
    if (busy) return;
    setOpen(false);
    setError(null);
    setImageUrl("");
  };

  return (
    <>
      <Tooltip title={open ? "" : "Cambiar imagen de la empresa"}>
        <Box
          sx={{ position: "relative", cursor: "pointer", flexShrink: 0 }}
          onClick={() => setOpen(true)}
        >
          <Avatar
            src={currentSrc || undefined}
            alt={company.name}
            variant="rounded"
            sx={{
              width: 56,
              height: 56,
              bgcolor: currentSrc ? "transparent" : "action.hover",
              color: "text.secondary",
              "& img": { objectFit: "contain" },
            }}
          >
            <BusinessIcon />
          </Avatar>
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              right: -8,
              bottom: -8,
              bgcolor: "background.paper",
              boxShadow: 1,
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Imagen de la empresa</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="stretch" sx={{ pt: 1 }}>
            <Box
              sx={{
                height: 140,
                borderRadius: 1,
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {busy ? (
                <CircularProgress size={28} />
              ) : currentSrc ? (
                <Box
                  component="img"
                  src={currentSrc}
                  alt={company.name}
                  sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Sin imagen
                </Typography>
              )}
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              hidden
              onChange={handleFileChange}
            />
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              Subir archivo
            </Button>

            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="…o importar desde URL"
                placeholder="https://…"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                disabled={busy}
              />
              <Button
                variant="outlined"
                onClick={() => uploadMutation.mutate({ url: imageUrl.trim() })}
                disabled={busy || !/^https?:\/\/.+/i.test(imageUrl.trim())}
              >
                Importar
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Se guarda como PNG de hasta 512×512 manteniendo la proporción. Se
              usa en exportaciones, invitaciones y enlaces públicos.
            </Typography>

            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {currentSrc && (
            <Button
              color="error"
              onClick={() => deleteMutation.mutate()}
              disabled={busy}
              sx={{ mr: "auto" }}
            >
              Eliminar imagen
            </Button>
          )}
          <Button onClick={handleClose} disabled={busy}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyImageEditor;
